import { NextPageContext } from "next";
import { destroyCookie, setCookie } from "nookies";
import LocalStore, { LocalStorageKeys } from "../localstorage/LocalStorage";
import { COOKIE_TOKEN_AGE, COOKIE_TOKEN_PATH, CookieKeys } from "../types/cookie-keys";
import { Errors } from "../types/api";
import { APPLICATION_ROUTES } from "../shared-logic/routes";
import { isAxiosError } from "./toError";

let expiredRedirectStarted = false;

/**
 * Picks the token to send to the API. The old token is only a fallback until it is upgraded.
 * @param cookies The parsed request or document cookies
 * @returns The session token, else the old token, else undefined
 */
export function getAuthToken(cookies: Record<string, string | undefined>): string | undefined {
    return cookies[CookieKeys.session] || cookies[CookieKeys.auth_token];
}

/**
 * Stores a session token. The old token cookie is never written.
 * @param ctx The page context on the server, null in the browser
 * @param sessionToken The session token returned by the API
 */
export function setSessionCookie(ctx: NextPageContext | null, sessionToken: string) {
    setCookie(ctx, CookieKeys.session, sessionToken, {
        path: COOKIE_TOKEN_PATH,
        maxAge: COOKIE_TOKEN_AGE
    });
}

/**
 * Removes every cookie and local storage entry the app knows about, both tokens included.
 */
export function clearLocalSession() {
    for (const name of Object.values(CookieKeys)) {
        destroyCookie(null, name, { path: COOKIE_TOKEN_PATH });
    }

    for (const key of Object.values(LocalStorageKeys)) {
        new LocalStore(key).unset();
    }
}

/**
 * Tells whether a path is login or one of its sub pages, where a redirect to login would loop.
 * @param pathname The path without its query string
 * @returns True on a login page
 */
export function isLoginPath(pathname: string) {
    return pathname === APPLICATION_ROUTES.LOGIN || pathname.startsWith(`${APPLICATION_ROUTES.LOGIN}/`);
}

/**
 * Clears the session and sends the user to login when an authenticated request fails with 401.
 * Requests without a token are skipped, so login and signup errors are left to their own handlers.
 * On a login page the session is only cleared, the user is already where the redirect would go.
 * @param error The error thrown by a query or mutation
 */
export function handleExpiredSession(error: unknown) {
    if (typeof window === "undefined" || expiredRedirectStarted) return;
    if (!isAxiosError(error) || error.response?.status !== Errors.Unauthorized) return;
    if (!error.config?.headers?.Authorization) return;

    clearLocalSession();

    const { pathname, search } = document.location;
    if (isLoginPath(pathname)) return;

    expiredRedirectStarted = true;
    document.location.href = `${APPLICATION_ROUTES.LOGIN}?redirect=${encodeURIComponent(pathname + search)}`;
}
